import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import watiLogo from "../assets/wati_logo_only.png";
import watiIncomingSound from "../assets/sounds/wati_incoming_sound.mp3";
import axios from "axios";
import dayjs from "dayjs";
import { Badge, Spin, Button, Card, Tag, Modal, message, notification, Collapse, Timeline, Switch, Avatar, Tooltip, Table, Image, Dropdown, Input, Select, Drawer, Empty, Radio, TimePicker, Checkbox, Popover } from "antd";
import { ReloadOutlined, CalendarOutlined, UserOutlined, PhoneOutlined, DollarOutlined, ClockCircleOutlined, TagOutlined, HourglassOutlined, CheckCircleOutlined, CarOutlined, CloseCircleOutlined, RollbackOutlined, SortDescendingOutlined, WhatsAppOutlined, PictureOutlined, ShopOutlined, GlobalOutlined, FireOutlined, CopyOutlined, CheckOutlined, PlusOutlined, DeleteOutlined, EditOutlined, PushpinOutlined, PushpinFilled, SendOutlined, WechatOutlined, SearchOutlined, AudioOutlined, AudioMutedOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileExcelOutlined, FileZipOutlined, FileTextOutlined, PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined, NotificationOutlined, StarFilled, StarOutlined, QuestionCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined, EyeOutlined, PaperClipOutlined, ThunderboltOutlined, SmileOutlined, DownOutlined, TranslationOutlined, SettingOutlined, SoundOutlined, MutedOutlined } from "@ant-design/icons";
import { DirhamSymbol } from "dirham/react";
import { API_DOMAIN, country_codes } from "../components/constants";
import { v4 as uuidv4 } from "uuid";
import { getCountryCode } from "../components/misc_functions";
import { AuthContext } from "../components/contexts/AuthContext";
import { useWatiSocket } from "../components/contexts/WatiSocketContext";
import { useSocket } from "../components/contexts/SocketContext";
import { useWatiChat } from "../components/contexts/WatiChatContext";
import { motion, AnimatePresence } from "framer-motion";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import {
  CHAT_API, CHAT_MESSAGES_API,
  SEND_VOICE_API, SEND_IMAGE_API, SEND_VIDEO_API, SEND_DOCUMENT_API,
  SEND_TEXT_API, SEND_TEMPLATE_API, MARK_AS_READ_API,
  WATI_FILE_RULES, WATI_ACCEPT_ATTR, WATI_ACCEPT_LABEL,
  TICKET_STATUS, TICKET_STATUS_STRING,
  GMAPS_KEY, GMAPS_LIBS,
  MB, KB,
} from "../components/qv4/constants";
import {
  _fetchAgents, _fetchQuickReplies, _fetchTemplates, _fetchCredits,
  _getTemplateHeaderSrc, _resolveTemplateBody, _previewTemplateBody,
  substituteQRText, mapChatItem, expiryDisplay, useIsMobile, CopyablePhone,
  isInternational, getCardCountry, AGENT_PALETTE, getAgentColor, agentInitials,
  _normalizeContact, _normalizeBroadcastItem, RTL_RE, isRtlText,
  parseAddresses, getDocMeta, fmtShortDate, fmtShortDateTime, _pastedImageToJpeg,
  _fetchSRMUsers,
} from "../components/qv4/helpers";
import {
  WaText, formatWaText, MsgStatus, MediaScrollCtx, ChatThemeCtx, ct,
  _mediaCache, _resolveMediaUrl, AudioPlayer, ContactsBubble,
  MsgMediaContent, BroadcastHeaderMedia, QuoteMediaThumb,
} from "../components/qv4/MessageParts";
import {
  TemplatesModal, BulkTemplatePreviewModal, BulkSummaryModal,
  TemplatePreviewModal, QuickRepliesModal, QuickReplyPreviewModal,
} from "../components/qv4/Modals";
import { PIPELINE_COLUMNS, groupQueriesByStatus, URGENCY_CFG } from "../components/qv4/pipeline";
import { AgentDropdown } from "../components/qv4/AgentDropdown";
import { ActiveChatCard } from "../components/qv4/ActiveChatCard";
import { OwnerBadge, GalleryAudioCard, GalleryThumb, getRecentReactions, addRecentReaction, EMOJI_CATS, EmojiPickerPanel } from "../components/qv4/ChatPanelGallery";
import { CustomerEditDrawer } from "../components/qv4/CustomerEditDrawer";
import { ChatPanelHeader } from "../components/qv4/ChatPanelHeader";
import { ChatPanelMessages } from "../components/qv4/ChatPanelMessages";
import { ActiveChatDrawer } from "../components/qv4/ActiveChatDrawer";
import { KanbanBoard } from "../components/qv4/KanbanBoard";
import { WorkflowTaskDrawer } from "../components/qv4/WorkflowTaskDrawer";
import { SendToSuppliersModal, preloadSupplierGroups } from "../components/qv4/SendToSuppliersModal";
import { EditDraftDrawer } from "../components/qv4/EditDraftDrawer";
import { NewQueryModal } from "../components/qv4/NewQueryModal";
import { PriceUpdateDrawer } from "../components/qv4/PriceUpdateDrawer";
import { PaymentLinkModal } from "../components/qv4/PaymentLinkModal";
import { ReassignModal } from "../components/qv4/ReassignModal";
import { UnparkModal } from "../components/qv4/UnparkModal";
import { CancelModal } from "../components/qv4/CancelModal";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const DRAWER_MIN = 400;
const PARKED_STATUSES = new Set(["parked", "on_hold", "postponed"]);

function QuerysetV4() {
  const { authState } = useContext(AuthContext);
  const head = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [querySet, setQuerySet] = useState([]);
  useEffect(() => { querySetRef.current = querySet; }, [querySet]);

  // ── Follow-up alarms ─────────────────────────────────────────────────────
  useEffect(() => {
    const alarms = alarmTimeoutsRef.current;
    const parked = querySet.filter(c => PARKED_STATUSES.has(c.status) && c.postponed_until);

    // Request browser notification permission once when parked cards appear
    if (parked.length > 0 && typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const fireAlarm = (card) => {
      const v = card.vehicle;
      const vehicleLabel = v
        ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
        : null;
      const key = `followup-${card.id}`;

      const scrollToCard = () => {
        const el = document.getElementById(`query-card-${card.query_id}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "box-shadow 0.4s ease";
        el.style.boxShadow = "0 0 0 3px #faad14";
        setTimeout(() => { el.style.boxShadow = ""; }, 2200);
      };

      notification.warning({
        key,
        message: `Follow-up due — #${card.query_id}`,
        description: vehicleLabel || card.remarks || "Follow-up time has arrived",
        duration: 0,
        placement: "topRight",
        btn: (
          <Button size="small" type="primary" onClick={() => { notification.destroy(key); setTimeout(scrollToCard, 350); }}>
            View task
          </Button>
        ),
      });

      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          const n = new Notification(`Follow-up due — #${card.query_id}`, {
            body: [vehicleLabel, card.remarks].filter(Boolean).join("\n") || "Follow-up time has arrived",
            icon: "/favicon.ico",
            tag: key,
          });
          n.onclick = () => { window.focus(); notification.destroy(key); scrollToCard(); };
        } catch {}
      }

      alarms.delete(card.id);
    };

    // Clear alarms for cards no longer parked / no longer have a follow-up time
    const parkedIds = new Set(parked.map(c => c.id));
    alarms.forEach(({ timeoutId }, cardId) => {
      if (!parkedIds.has(cardId)) { clearTimeout(timeoutId); alarms.delete(cardId); }
    });

    // Set or update alarms for currently parked cards
    parked.forEach(card => {
      const until = card.postponed_until;
      const existing = alarms.get(card.id);
      if (existing?.until === until) return; // unchanged, skip

      if (existing) clearTimeout(existing.timeoutId); // time changed, reset

      const msUntil = dayjs.utc(until.replace(" ", "T")).diff(dayjs.utc(), "ms");
      if (msUntil <= 0) return; // already past

      const timeoutId = setTimeout(() => fireAlarm(card), msUntil);
      alarms.set(card.id, { timeoutId, until });
    });
  }, [querySet]);

  // Clear all alarms on unmount
  useEffect(() => () => {
    alarmTimeoutsRef.current.forEach(({ timeoutId }) => clearTimeout(timeoutId));
  }, []);
  const [loading, setLoading] = useState(false);
  const [querySearch, setQuerySearch] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [priceCard, setPriceCard] = useState(null);
  const [paymentCard, setPaymentCard] = useState(null);
  const [reassignCard, setReassignCard] = useState(null);
  const [unparkCard, setUnparkCard] = useState(null);
  const [cancelCard, setCancelCard] = useState(null); // { queryPk, queryId }
  const [urgencySortOn, setUrgencySortOn] = useState(false);
  const [readReceiptsOn, setReadReceiptsOn] = useState(() => localStorage.getItem("wati_read_receipts") !== "false");
  const [notifSoundEnabled, setNotifSoundEnabled] = useState(() => localStorage.getItem("wati_qv4_sound_enabled") !== "false");
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [newQueryPickerOpen, setNewQueryPickerOpen] = useState(false);
  const [newQueryCust, setNewQueryCust] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    const stored = localStorage.getItem("qv4_hidden_cols");
    if (stored !== null) {
      try { return new Set(JSON.parse(stored)); }
      catch { return new Set(); }
    }
    return new Set(["waiting_purchase", "purchased", "out_for_delivery"]);
  });
  const [activeChats, setActiveChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchFilter, setChatSearchFilter] = useState(0);
  const [selectedChat, setSelectedChat] = useState(null);
  const [bulkTmplOpen, setBulkTmplOpen] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState(null);
  const [bulkRecipients, setBulkRecipients] = useState([]);
  const [bulkSummaryOpen, setBulkSummaryOpen] = useState(false);
  const [bulkTemplates, setBulkTemplates] = useState([]);
  const [bulkTmplLoading, setBulkTmplLoading] = useState(false);
  const [chatDrawerPinned, setChatDrawerPinned] = useState(false);
  const [chatDrawerWidth, setChatDrawerWidth] = useState(DRAWER_MIN);


  const [sendingCards, setSendingCards] = useState({});
  useEffect(() => { sendingCardsRef.current = sendingCards; }, [sendingCards]);
  const [sendCard, setSendCard] = useState(null);
  const [editDraftCard, setEditDraftCard] = useState(null);

  const [approvalDecidedTick, setApprovalDecidedTick] = useState(0);
  const [activeApprovalsMap, setActiveApprovalsMap] = useState({});
  const { watiChatUpdates, queryUpdates } = useWatiSocket();
  const { socket: mainSocket } = useSocket();
  const { qv4Chatlist, qv4ChatsLoading } = useWatiChat();
  const watiProcessedCountRef = useRef(0);
  const queryProcessedCountRef = useRef(0);
  const sendingCardsRef = useRef({});
  const querySetRef = useRef([]);
  const activeChatsRef = useRef([]);
  const pendingEnrichRef = useRef(new Set());
  const authStateRef = useRef(authState);
  useEffect(() => { authStateRef.current = authState; }, [authState]);
  const alarmTimeoutsRef = useRef(new Map()); // cardId → { timeoutId, until }
  const [flashingChatIds, setFlashingChatIds] = useState(new Set());
  const selectedChatRef = useRef(null);
  useEffect(() => { selectedChatRef.current = selectedChat?.id ?? null; }, [selectedChat]);
  useEffect(() => { activeChatsRef.current = activeChats; }, [activeChats]);

  const enrichNewChatCard = async (convId, phone, name) => {
    if (!phone || pendingEnrichRef.current.has(convId)) return;
    pendingEnrichRef.current.add(convId);
    try {
      const headers = { accessToken: localStorage.getItem("accessToken") };
      const res = await axios.get(`${API_DOMAIN}/customers/get_full_profile`, {
        params: { phone },
        headers,
      });
      if (res.data.success && res.data.data?.length > 0) {
        const profile = res.data.data[0];
        const custType = profile.type ?? null;
        const custName = profile.customer_name || null;
        const patch = c => c.id === convId
          ? { ...c, ...(custType !== null && { customer_type: custType }), ...(custName && { customer_name: custName }) }
          : c;
        setActiveChats(prev => prev.map(patch));
        setSelectedChat(c => c?.id === convId ? patch(c) : c);
      } else {
        await axios.post(
          `${API_DOMAIN}/whatsapp/customer_add`,
          { name: name || phone, number: phone },
          { headers }
        );
        notification.info({
          message: "New customer saved",
          description: `"${name || phone}" was automatically added to your customer list. You can set their type in the chat drawer.`,
          placement: "topRight",
          duration: 6,
          key: `new-cust-${convId}`,
        });
      }
    } catch { }
    finally { pendingEnrichRef.current.delete(convId); }
  };

  const selectAndScrollToChat = (chat) => {
    setSelectedChat(chat);
    setFlashingChatIds(prev => new Set([...prev, chat.id]));
    setTimeout(() => setFlashingChatIds(prev => { const s = new Set(prev); s.delete(chat.id); return s; }), 1400);
    setTimeout(() => {
      document.getElementById(`chat-card-${chat.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const openChatByPhone = async (phone) => {
    const normalized = (phone || "").replace(/\D/g, "");
    if (!normalized) return;

    const matchPhone = (c) => (c.phone || "").replace(/\D/g, "") === normalized;

    // Step 1 — already loaded
    const found = activeChats.find(matchPhone);
    if (found) { selectAndScrollToChat(found); return; }

    // Step 2 — load more pages (up to 3 extra batches)
    if (hasMoreChats) {
      message.loading({ content: "Looking for chat…", key: "chat-phone-lookup", duration: 0 });
      let currentList = [...activeChats];
      let moreAvailable = true;
      for (let i = 0; i < 3 && moreAvailable; i++) {
        try {
          const lastId = currentList[currentList.length - 1]?.conversationId;
          const res = await axios.post(CHAT_API, { channelIds: "", search_filter: chatSearchFilter, search: chatSearch.trim(), lastId, userId: authState.id }, head);
          if (res.data.success) {
            const raw = res.data.data?.final_data || [];
            if (raw.length === 0) { moreAvailable = false; break; }
            const existingIds = new Set(currentList.map(c => c.id));
            const newItems = raw.map(mapChatItem).filter(c => !existingIds.has(c.id));
            currentList = [...currentList, ...newItems];
            setActiveChats(currentList);
            if (raw.length < 100) { moreAvailable = false; setHasMoreChats(false); }
            const hit = newItems.find(matchPhone);
            if (hit) {
              message.destroy("chat-phone-lookup");
              setTimeout(() => selectAndScrollToChat(hit), 80);
              return;
            }
          } else break;
        } catch { break; }
      }
      message.destroy("chat-phone-lookup");
    }

    // Step 3 — search by phone, replace the chat list
    message.loading({ content: "Searching by phone number…", key: "chat-phone-lookup", duration: 0 });
    try {
      const res = await axios.post(CHAT_API, { channelIds: "", search_filter: 0, search: normalized, userId: authState.id }, head);
      message.destroy("chat-phone-lookup");
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const mapped = raw.map(mapChatItem);
        setActiveChats(mapped);
        setChatSearch(normalized);
        setChatSearchFilter(0);
        setHasMoreChats(raw.length >= 100);
        const hit = mapped.find(matchPhone);
        if (hit) {
          setTimeout(() => selectAndScrollToChat(hit), 80);
        } else {
          message.warning("No active chat found for this customer.");
        }
      }
    } catch {
      message.destroy("chat-phone-lookup");
      message.error("Failed to find chat.");
    }
  };

  useEffect(() => {
    if (!selectedChat?.id) return;
    if (localStorage.getItem("wati_read_receipts") === "false") return;
    setActiveChats(prev => prev.map(c => c.id === selectedChat.id ? { ...c, unread_count: 0 } : c));
  }, [selectedChat?.id]);

  useEffect(() => {
    if (watiChatUpdates.length <= watiProcessedCountRef.current) return;
    const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
    watiProcessedCountRef.current = watiChatUpdates.length;

    newUpdates.forEach(update => {
      // WATI hub protocol sends some events as arrays: [{type, target, arguments}]
      if (update.target === "catchSelectedConversation") {
        const chatId = update["0"];
        if (chatId) {
          setActiveChats(prev => prev.map(c => c.conversationId === chatId ? { ...c, unread_count: 0 } : c));
        }
        return;
      }

      if (update.target === "updatingConversation") {
        const conversationId = update["0"];
        const ticket = update["1"];
        if (!conversationId || !ticket) return;
        const applyPatch = c => {
          if (c.conversationId !== conversationId) return c;
          // When the window just reopened (status → Open, old expiry was in the past), reset expiry
          const expiry = (ticket.status === 1 && c.expiry?.isValid() && c.expiry.isBefore(dayjs()))
            ? dayjs().add(24, "hour")
            : c.expiry;
          return {
            ...c,
            ticket_status: ticket.status ?? null,
            agent: ticket.assignedFullName || null,
            topic: ticket.topicName || null,
            expiry,
            raw: c.raw ? { ...c.raw, ticket } : c.raw,
          };
        };
        setActiveChats(prev => prev.map(applyPatch));
        setSelectedChat(c => c ? applyPatch(c) : c);
        return;
      }

      // ── Broadcast message sent to a contact — upsert its chat card ────────────
      if (update.target === "updatingConversationByBroadcast") {
        const conv = update["0"];
        if (conv?.channelId !== null && conv?.channelId !== undefined) return;
        if (!conv?.id) return;
        const isOpen = selectedChatRef.current === conv.id;
        const updatedChat = mapChatItem({
          id: conv.id,
          name: conv.name,
          lastUpdated: conv.lastUpdated,
          lastMessageDate: conv.lastMessageDate,
          lastIncomingDate: conv.lastIncomingDate,
          type: conv.type,
          text: update["1"]?.finalText || conv.text,
          messageType: "broadcastmessage",
          unreadCount: isOpen ? 0 : (conv.unreadCount || 0),
          isStar: conv.isStar,
          isBlocked: conv.isBlocked,
          whatsappId: conv.whatsappId,
          contactId: conv.contactId,
          activeTicketType: conv.activeTicketType,
          ticket: conv.ticket,
          replyDeadline: conv.replyDeadline,
          channelName: conv.channelName,
        });
        const isBrdNew = !activeChatsRef.current.some(c => c.id === updatedChat.id);
        setActiveChats(prev => {
          const exists = prev.some(c => c.id === updatedChat.id);
          const next = exists
            ? prev.map(c => c.id === updatedChat.id ? updatedChat : c)
            : [...prev, updatedChat];
          return [...next].sort((a, b) => {
            const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
            const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
            return bT - aT;
          });
        });
        if (isOpen) setSelectedChat(updatedChat);
        if (isBrdNew) enrichNewChatCard(updatedChat.id, updatedChat.phone, updatedChat.customer_name);
        return;
      }

      if (update.target !== "sendTextMessage") return;
      if (chatSearch.trim()) return;
      const channelId = update["0"]?.message?.conViewModel?.channelId;
      const mTypeBrd = update["0"]?.messageType || "text";
      if (channelId !== null && mTypeBrd !== "template") return;

      // conViewModel is present for incoming messages; outgoing messages from the
      // operator may omit it entirely — access safely and fall back to the top-level
      // conversationId that WATI always provides.
      const conv = update["0"]?.message?.conViewModel ?? null;
      const convId = conv?.id ?? update["0"]?.conversationId;
      const isIncoming = update["0"]?.message?.isOwner === false;
      const isOpen = selectedChatRef.current === convId;

      if (!convId) return;

      if (conv) {
        // Incoming path — full conViewModel present, rebuild chat object normally.
        const updatedChat = mapChatItem({
          id: conv.id,
          name: conv.name,
          lastUpdated: conv.lastUpdated,
          lastMessageDate: conv.lastMessageDate,
          lastIncomingDate: conv.lastIncomingDate,
          type: conv.type,
          text: conv.text || update["0"].message.text,
          messageType: update["0"].messageType,
          unreadCount: isOpen ? 0 : conv.unreadCount,
          isStar: conv.isStar,
          isBlocked: conv.isBlocked,
          whatsappId: conv.whatsappId,
          contactId: conv.contactId,
          activeTicketType: conv.activeTicketType,
          ticket: conv.ticket,
          replyDeadline: conv.replyDeadline,
          channelName: conv.channelName,
        });
        const isMsgNew = !activeChatsRef.current.some(c => c.id === updatedChat.id);
        setActiveChats(prev => {
          const exists = prev.some(c => c.id === updatedChat.id);
          const next = exists
            ? prev.map(c => c.id === updatedChat.id ? updatedChat : c)
            : [...prev, updatedChat];
          return [...next].sort((a, b) => {
            const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
            const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
            return bT - aT;
          });
        });
        if (isOpen) setSelectedChat(updatedChat);
        if (isMsgNew) enrichNewChatCard(updatedChat.id, updatedChat.phone, updatedChat.customer_name);
      } else {
        // Outgoing path — no conViewModel, patch last_message + time on the existing card.
        const msgType = (update["0"].messageType || "text").toLowerCase();
        const rawText = update["0"]?.message?.text ?? null;
        let lastMsg;
        if (msgType === "audio") lastMsg = "Voice message";
        else if (msgType === "image") lastMsg = rawText || "Photo";
        else if (msgType === "video") lastMsg = rawText || "Video";
        else if (msgType === "document") lastMsg = rawText || "Document";
        else lastMsg = rawText;

        if (lastMsg != null) {
          try { sessionStorage.setItem(`wati_out_${convId}`, JSON.stringify({ text: lastMsg, time: Date.now() })); } catch {}
          setActiveChats(prev => {
            const next = prev.map(c =>
              (c.id === convId || c.conversationId === convId)
                ? { ...c, last_message: lastMsg, last_message_time: dayjs() }
                : c
            );
            return [...next].sort((a, b) => {
              const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
              const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
              return bT - aT;
            });
          });
        }
      }

      if (isIncoming) {
        setFlashingChatIds(prev => new Set([...prev, convId]));
        setTimeout(() => setFlashingChatIds(prev => { const n = new Set(prev); n.delete(convId); return n; }), 2000);
      }
    });
  }, [watiChatUpdates]);

  useEffect(() => {
    if (queryUpdates.length <= queryProcessedCountRef.current) return;
    const newUpdates = queryUpdates.slice(queryProcessedCountRef.current);
    queryProcessedCountRef.current = queryUpdates.length;
    newUpdates.forEach(update => {
      if (update.event === "supplier_sent") {
        setSendingCards(prev => ({ ...prev, [update.query_pk]: { sent: update.sent, total: update.total } }));
      } else if (update.event === "send_complete") {
        setSendingCards(prev => { const next = { ...prev }; delete next[update.query_pk]; return next; });
        setQuerySet(prev => prev.map(card =>
          card.id === update.query_pk ? { ...card, status: update.new_status || "waiting_for_price" } : card
        ));
      }
    });
  }, [queryUpdates]);

  useEffect(() => {
    if (!mainSocket) return;
    const handleProgress = ({ task_id, processed, total }) => {
      setSendingCards(prev => ({ ...prev, [task_id]: { sent: processed, total } }));
    };
    const handleUpdated = (data) => {
      if (!data?.success) return;
      const t = data.data;

      // If this task was in the sending queue, it just completed — show a notification
      const sendInfo = sendingCardsRef.current[t.task_id];
      if (sendInfo) {
        const card = querySetRef.current.find(c => c.id === t.task_id);
        const queryId = card?.query_id ?? t.query_id;
        const v = card?.vehicle;
        const vehicleLabel = v
          ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
          : null;
        // Auto-scroll and flash the card in its new column
        setTimeout(() => {
          const el = document.getElementById(`query-card-${queryId}`);
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.style.transition = "box-shadow 0.3s ease";
          el.style.boxShadow = "0 0 0 3px #1677ff";
          setTimeout(() => { el.style.boxShadow = ""; }, 2000);
        }, 400);
        notification.success({
          message: `Query #${queryId} sent`,
          description: `Sent to ${sendInfo.total} supplier${sendInfo.total !== 1 ? "s" : ""}${vehicleLabel ? ` · ${vehicleLabel}` : ""}.`,
          duration: 5,
          placement: "topRight",
        });
      }

      setSendingCards(prev => { const next = { ...prev }; delete next[t.task_id]; return next; });
      setQuerySet(prev => prev.map(card => {
        if (card.id !== t.task_id) return card;
        return {
          ...card,
          status: t.status ?? card.status,
          urgency: t.urgency ?? card.urgency,
          vehicle: t.vehicle ?? card.vehicle,
          assigned_user: t.assigned_user ?? card.assigned_user,
          query_parts: t.query_parts ?? card.query_parts,
          images: t.images ?? card.images,
          remarks: t.remarks ?? card.remarks,
          delivered_at: t.delivered_at ?? card.delivered_at,
          warranty_summary: t.warranty_summary ?? card.warranty_summary,
        };
      }));
    };
    const handleCreated = (data) => {
      if (!data?.success) return;
      const t = data.data;
      setQuerySet(prev => {
        if (prev.some(c => c.id === t.task_id || c.query_id === t.query_id)) return prev;
        return [{
          id: t.task_id,
          query_pk: t.task_id,
          query_id: t.query_id,
          status: t.status ?? "draft",
          urgency: t.urgency ?? 2,
          created_at: new Date().toISOString(),
          vehicle: t.vehicle ?? null,
          assigned_user: t.assigned_user ?? null,
          customer: t.customer ?? null,
          query_parts: t.query_parts ?? [],
          images: t.images ?? [],
          remarks: t.remarks ?? "",
          has_after_sales: false,
        }, ...prev];
      });
    };
    const handleQuotesUpdated = (data) => {
      if (!data?.success) return;
      const { task_id, event: ev } = data.data ?? {};
      if (!task_id) return;
      setQuerySet(prev => prev.map(card => {
        if (card.id !== task_id) return card;
        const delta = ev === "quote_disabled" ? -1 : ev === "quote_created" ? 1 : 0;
        if (delta === 0) return card;
        const next = (card.parts_with_quotes ?? 0) + delta;
        return { ...card, parts_with_quotes: Math.max(0, Math.min(next, card.total_parts ?? next)) };
      }));
    };

    const handlePartsUpdated = (data) => {
      if (!data?.success) return;
      const { task_id, part, event: ev } = data.data ?? {};
      if (!task_id || !part) return;
      setQuerySet(prev => prev.map(card => {
        if (card.id !== task_id) return card;
        if (ev === "part_disabled") {
          const total = Math.max(0, (card.total_parts ?? 1) - 1);
          return { ...card, total_parts: total };
        }
        return card;
      }));
    };

    const handleApprovalCreated = (data) => {
      if (data?.query_pk) setActiveApprovalsMap(m => ({ ...m, [data.query_pk]: { status: "pending", query_pk: data.query_pk } }));
      if (!authStateRef.current?.user?.admin) return;
      const queryId = data?.query_id;
      notification.warning({
        message: "Quote awaiting approval",
        description: queryId ? `Query #${queryId} — review and approve or reject.` : "A quote is pending your approval.",
        duration: 6,
        placement: "topRight",
      });
    };

    const handleApprovalApproved = (data) => {
      setApprovalDecidedTick(t => t + 1);
      if (data?.query_pk) setActiveApprovalsMap(m => { const n = { ...m }; delete n[data.query_pk]; return n; });
      notification.success({
        message: "Your quote was approved",
        description: data?.query_id ? `Query #${data.query_id} — quote has been sent to the customer.` : "Quote has been sent to the customer.",
        duration: 6,
        placement: "topRight",
      });
    };

    const handleApprovalRejected = (data) => {
      setApprovalDecidedTick(t => t + 1);
      if (data?.query_pk) setActiveApprovalsMap(m => ({ ...m, [data.query_pk]: { status: "rejected", query_pk: data.query_pk, decision_reason: data.decision_reason, decided_by: data.decided_by } }));
      const key = `rejection-${Date.now()}`;
      notification.error({
        key,
        message: `Quote rejected${data?.query_id ? ` — Query #${data.query_id}` : ""}`,
        description: data?.decision_reason || "Your submitted quote was rejected.",
        duration: 0,
        placement: "topRight",
        btn: data?.query_pk ? (
          <Button
            size="small" type="primary" danger
            onClick={() => {
              const fullCard = querySetRef.current.find(c => (c.id ?? c.query_pk) === data.query_pk);
              setPriceCard(fullCard || { id: data.query_pk, query_id: data.query_id });
              notification.destroy(key);
            }}
          >
            Open Quote
          </Button>
        ) : undefined,
      });
    };

    mainSocket.on("query_supplier_send_progress", handleProgress);
    mainSocket.on("queryset_v2_updated", handleUpdated);
    mainSocket.on("queryset_v2_created", handleCreated);
    mainSocket.on("query:quotes_updated", handleQuotesUpdated);
    mainSocket.on("query:parts_updated", handlePartsUpdated);
    const handleApprovalDeleted = (data) => {
      const queryPk = data?.data?.query_pk;
      if (queryPk) setActiveApprovalsMap(m => { const n = { ...m }; delete n[queryPk]; return n; });
      else fetchActiveApprovals();
    };

    // query:updated / query:task_moved — handles reassignments + other task-level updates
    const notifyAssigned = (id, queryId, vehicle) => {
      const v = vehicle;
      const vehicleLabel = v
        ? [v.brand_name || v.brand, v.model_name || v.model, v.year ? `(${v.year})` : ""].filter(Boolean).join(" ")
        : null;
      const notifKey = `task-assigned-${id}`;
      notification.info({
        key: notifKey,
        message: `Task #${queryId} assigned to you`,
        description: vehicleLabel || "A new task has been assigned to you",
        duration: 10,
        placement: "topRight",
        btn: (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              notification.destroy(notifKey);
              setTimeout(() => {
                const el = document.getElementById(`query-card-${queryId}`);
                if (!el) return;
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.style.transition = "box-shadow 0.4s ease";
                el.style.boxShadow = "0 0 0 3px #1677ff";
                setTimeout(() => { el.style.boxShadow = ""; }, 2200);
              }, 350);
            }}
          >
            View task
          </Button>
        ),
      });
    };

    const handleQueryUpdated = (data) => {
      if (!data?.success) return;
      const t = data.data;
      // For query:task_moved, data is a movementRow — use query_pk as the task id.
      // For query:updated, data is the task itself — use id directly.
      const id = t.task_id ?? t.query_pk ?? t.id;
      if (!id) return;
      const isAdmin = authStateRef.current?.user?.admin;
      const currentUserId = authStateRef.current?.id;
      const newAssigneeId = t.assigned_user?.id;

      // Cancelled tasks: t.status (query:updated) or t.to_status (query:task_moved)
      if (t.status === "cancelled" || t.to_status === "cancelled") {
        const taskId = t.task_id ?? t.query_pk ?? (t.status ? t.id : null);
        if (taskId) {
          setQuerySet(prev => prev.filter(card => card.id !== taskId));
          setSelectedQuery(sel => (sel?.id ?? sel?.query_pk) === taskId ? null : sel);
        }
        return;
      }

      const exists = querySetRef.current.some(card => card.id === id);

      if (exists) {
        if (!isAdmin && newAssigneeId && newAssigneeId !== currentUserId) {
          // Non-admin: task reassigned away — remove from board and close drawer if open
          setSelectedQuery(sel => {
            const selId = sel?.id ?? sel?.query_pk;
            return selId === id ? null : sel;
          });
          setQuerySet(prev => prev.filter(card => card.id !== id));
        } else {
          setQuerySet(prev => prev.map(card => {
            if (card.id !== id) return card;
            return {
              ...card,
              status: t.status ?? card.status,
              urgency: t.urgency ?? card.urgency,
              vehicle: t.vehicle ?? card.vehicle,
              assigned_user: t.assigned_user ?? card.assigned_user,
              query_parts: t.query_parts ?? card.query_parts,
              images: t.images ?? card.images,
              remarks: t.remarks ?? card.remarks,
              total_parts: t.total_parts ?? card.total_parts,
              parts_with_quotes: t.parts_with_quotes ?? card.parts_with_quotes,
              has_after_sales: t.has_after_sales ?? card.has_after_sales,
            };
          }));
          // Notify if this user is the new assignee (card was already on board — admin path)
          if (newAssigneeId && newAssigneeId === currentUserId) {
            notifyAssigned(id, t.query_id, t.vehicle);
          }
        }
      } else if (isAdmin || newAssigneeId === currentUserId) {
        // Task not yet on this board — add it
        setQuerySet(prev => [{
          id,
          query_pk: id,
          query_id: t.query_id,
          status: t.status ?? "active",
          urgency: t.urgency ?? 2,
          created_at: t.created_at ?? new Date().toISOString(),
          vehicle: t.vehicle ?? null,
          assigned_user: t.assigned_user ?? null,
          customer: t.customer ?? null,
          query_parts: t.query_parts ?? [],
          images: t.images ?? [],
          remarks: t.remarks ?? "",
          total_parts: t.total_parts ?? 0,
          parts_with_quotes: t.parts_with_quotes ?? 0,
          has_after_sales: t.has_after_sales ?? false,
        }, ...prev]);
        // Notify regardless of admin status — they're the new assignee
        if (newAssigneeId === currentUserId) {
          notifyAssigned(id, t.query_id, t.vehicle);
        }
      }
    };
    // query:quote_sent — payload is { success, data: { query_pk, query_status, ... } }
    const handleQuoteSent = (data) => {
      const id = data?.data?.query_pk;
      const status = data?.data?.query_status;
      if (!id || !status) return;
      setQuerySet(prev => prev.map(card =>
        card.id === id ? { ...card, status } : card
      ));
    };

    mainSocket.on("quote_approval:created", handleApprovalCreated);
    mainSocket.on("quote_approval:approved", handleApprovalApproved);
    mainSocket.on("quote_approval:rejected", handleApprovalRejected);
    mainSocket.on("quote_approval:deleted", handleApprovalDeleted);
    mainSocket.on("query:updated", handleQueryUpdated);
    mainSocket.on("query:task_moved", handleQueryUpdated);
    mainSocket.on("query:quote_sent", handleQuoteSent);
    return () => {
      mainSocket.off("query_supplier_send_progress", handleProgress);
      mainSocket.off("queryset_v2_updated", handleUpdated);
      mainSocket.off("queryset_v2_created", handleCreated);
      mainSocket.off("query:quotes_updated", handleQuotesUpdated);
      mainSocket.off("query:parts_updated", handlePartsUpdated);
      mainSocket.off("quote_approval:created", handleApprovalCreated);
      mainSocket.off("quote_approval:approved", handleApprovalApproved);
      mainSocket.off("quote_approval:rejected", handleApprovalRejected);
      mainSocket.off("quote_approval:deleted", handleApprovalDeleted);
      mainSocket.off("query:updated", handleQueryUpdated);
      mainSocket.off("query:task_moved", handleQueryUpdated);
      mainSocket.off("query:quote_sent", handleQuoteSent);
    };
  }, [mainSocket]); // eslint-disable-line

  const handlePartsLoaded = (taskId, parts) => {
    const total = parts.length;
    const withQuotes = parts.filter(p => (p.supplier_quotes ?? []).length > 0).length;
    setQuerySet(prev => prev.map(c =>
      c.id === taskId ? { ...c, total_parts: total, parts_with_quotes: withQuotes } : c
    ));
  };

  const handleQuerySent = (queryPk, supplierCount) => {
    setSendingCards(prev => ({ ...prev, [queryPk]: { sent: 0, total: supplierCount } }));
    setSelectedQuery(null);
  };


  const fetchActiveApprovals = async () => {
    try {
      const res = await axios.get(`${API_DOMAIN}/queries/approvals/active`, head);
      const list = res.data?.data ?? [];
      const map = {};
      list.forEach(a => { map[a.query_pk] = a; });
      setActiveApprovalsMap(map);
    } catch {}
  };

  useEffect(() => {
    fetchActiveApprovals();
  }, [authState?.user?.admin]); // eslint-disable-line

  const queryMatchesByPhone = useMemo(() => {
    const map = {};
    querySet.forEach(card => {
      const n = (card.customer?.customer_mobile || card.customer_mobile || "").replace(/\D/g, "");
      if (!n) return;
      if (!map[n]) map[n] = [];
      map[n].push(card);
    });
    return map;
  }, [querySet]);

  const scrollToSingleQuery = (query_id) => {
    const el = document.getElementById(`query-card-${query_id}`);
    if (!el) { message.info("That card isn't visible with current filters."); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.style.transition = "box-shadow 0.25s";
    el.style.boxShadow = "0 0 0 3px #1677ff";
    setTimeout(() => { el.style.boxShadow = ""; }, 1500);
  };

  const filteredQuerySet = useMemo(() => {
    if (!selectedAgents.length) return querySet;
    return querySet.filter(c => selectedAgents.includes(c.assigned_user?.id ?? c.assigned_to));
  }, [querySet, selectedAgents]);

  const grouped = useMemo(() => groupQueriesByStatus(filteredQuerySet), [filteredQuerySet]);

  const handleUrgencyChange = async (card, urgency) => {
    try {
      const res = await axios.post(
        `${API_DOMAIN}/query_workflow/update_urgency`,
        { task_id: card.id ?? card.query_pk, urgency },
        head
      );
      if (res.data.success) fetchQueries();
      else message.error(res.data.message || "Failed to update urgency");
    } catch {
      message.error("Failed to update urgency");
    }
  };

  const fetchQueries = async (search = querySearch) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: authState.id });
      if (search?.trim()) params.set("search", search.trim());
      const res = await axios.get(
        `${API_DOMAIN}/queryset_v2/get?${params}`,
        head
      );
      if (res.data.success) setQuerySet(res.data.data || []);
    } catch {
      message.error("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
    preloadSupplierGroups();
    _fetchSRMUsers().then(list => setAgentOptions(list.map(u => ({ value: u.id, label: u.fullName }))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter) => {
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const res = await axios.post(CHAT_API, { channelIds: "", search_filter: filter, search: search.trim(), userId: authState.id }, head);
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        setActiveChats(raw.map(mapChatItem));
        if (raw.length < 100) setHasMoreChats(false);
      }
    } catch {
      message.error("Failed to load active chats.");
    } finally {
      setChatsLoading(false);
    }
  };

  const loadMoreChats = async () => {
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const lastId = activeChats[activeChats.length - 1]?.conversationId;
      const res = await axios.post(CHAT_API, { channelIds: "", search_filter: chatSearchFilter, search: chatSearch.trim(), lastId, userId: authState.id }, head);
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        if (raw.length === 0) {
          setHasMoreChats(false);
        } else {
          setActiveChats(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newItems = raw.map(mapChatItem).filter(c => !existingIds.has(c.id));
            return [...prev, ...newItems];
          });
          if (raw.length < 100) setHasMoreChats(false);
        }
      }
    } catch {
      // silent — don't interrupt the UX on pagination failure
    } finally {
      setChatsLoadingMore(false);
    }
  };

  // Seed chat list from global cache if available; fall back to direct fetch
  useEffect(() => {
    if (qv4Chatlist.length > 0) {
      setActiveChats(qv4Chatlist.map(mapChatItem));
      setHasMoreChats(qv4Chatlist.length >= 100);
    } else if (!qv4ChatsLoading) {
      // Background load finished with no data (auth not ready yet or error) — fetch directly
      fetchActiveChats();
    }
    // If qv4ChatsLoading is still true, the watcher below will handle it
    // eslint-disable-next-line
  }, []);

  // Race-condition watcher: cache arrives after this component mounted
  const chatsCachedRef = useRef(false);
  useEffect(() => {
    if (chatsCachedRef.current || qv4Chatlist.length === 0) return;
    chatsCachedRef.current = true;
    // Only seed if we haven't already gotten data from our own fetch
    setActiveChats(prev => prev.length === 0 ? qv4Chatlist.map(mapChatItem) : prev);
    setHasMoreChats(prev => qv4Chatlist.length < 100 ? false : prev);
    setChatsLoading(false);
  }, [qv4Chatlist]); // eslint-disable-line





  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 100,
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>Sales Pipeline</span>
        <Input.Search
          placeholder="Search tasks…"
          allowClear
          value={querySearch}
          onChange={e => setQuerySearch(e.target.value)}
          onSearch={val => fetchQueries(val)}
          style={{ width: 220 }}
          size="small"
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="All agents"
          options={agentOptions}
          value={selectedAgents}
          onChange={setSelectedAgents}
          maxTagCount="responsive"
          style={{ minWidth: 160, maxWidth: 280 }}
          size="small"
          filterOption={(input, opt) => (opt.label ?? "").toLowerCase().includes(input.toLowerCase())}
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchQueries()} loading={loading} size="small">
          Reload
        </Button>
        <Button icon={<PlusOutlined />} type="primary" size="small" onClick={() => setNewQueryPickerOpen(true)}>
          New Query
        </Button>
        <Badge count={filteredQuerySet.length} overflowCount={99999} style={{ backgroundColor: "#1677ff" }} showZero />
        <span style={{ color: "#8c8c8c", fontSize: 13 }}>tasks</span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <Tooltip title="Urgent First" placement="bottom">
            <Button icon={<FireOutlined />} type={urgencySortOn ? "primary" : "default"} onClick={() => setUrgencySortOn(v => !v)} size="small" style={{ padding: "0 10px" }} danger={urgencySortOn} />
          </Tooltip>
          <Tooltip title="Settings" placement="bottom">
            <Button
              icon={<SettingOutlined />}
              size="small"
              style={{ padding: "0 10px", borderColor: !notifSoundEnabled ? "#faad14" : undefined, color: !notifSoundEnabled ? "#faad14" : undefined }}
              onClick={() => setSettingsModalOpen(true)}
            />
          </Tooltip>
        </div>
      </div>

      {/* Settings modal */}
      <Modal
        title="Settings"
        open={settingsModalOpen}
        onCancel={() => setSettingsModalOpen(false)}
        footer={null}
        width={400}
      >
        {/* Sound toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {notifSoundEnabled ? <SoundOutlined style={{ fontSize: 18, color: "#1677ff" }} /> : <MutedOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />}
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Incoming message sound</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Play a sound when a new WhatsApp message arrives</div>
            </div>
          </div>
          <Switch
            checked={notifSoundEnabled}
            onChange={(val) => {
              setNotifSoundEnabled(val);
              localStorage.setItem("wati_qv4_sound_enabled", val ? "true" : "false");
            }}
          />
        </div>

        {/* Column visibility */}
        <div style={{ paddingTop: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Visible columns</div>
          {[
            {
              group: "Enquiry",
              keys: ["draft", "waiting_for_price", "waiting_customer_confirmation", "confirmed_waiting_payment", "paid"],
            },
            {
              group: "Fulfilment",
              keys: ["waiting_purchase", "purchased", "out_for_delivery", "completed"],
            },
            {
              group: "Side states",
              keys: ["cancelled"],
              note: "Parked is always shown",
            },
          ].map(({ group, keys, note }) => (
            <div key={group} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                {group}{note && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, fontSize: 11 }}>· {note}</span>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 0" }}>
                {keys.map(key => {
                  const col = PIPELINE_COLUMNS.find(c => c.key === key);
                  if (!col) return null;
                  const visible = !hiddenColumns.has(key);
                  return (
                    <div
                      key={key}
                      onClick={() => {
                        const next = new Set(hiddenColumns);
                        visible ? next.add(key) : next.delete(key);
                        setHiddenColumns(next);
                        localStorage.setItem("qv4_hidden_cols", JSON.stringify([...next]));
                      }}
                      style={{
                        width: "50%", display: "flex", alignItems: "center", gap: 8,
                        cursor: "pointer", padding: "3px 0", userSelect: "none",
                      }}
                    >
                      <span style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${visible ? col.color : "#d9d9d9"}`,
                        background: visible ? col.color : "transparent",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s",
                      }}>
                        {visible && <span style={{ width: 6, height: 6, background: "#fff", borderRadius: 1, display: "block" }} />}
                      </span>
                      <span style={{ fontSize: 13, color: visible ? "#262626" : "#bfbfbf", transition: "color 0.15s" }}>
                        {col.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <KanbanBoard
        grouped={grouped}
        urgencySortOn={urgencySortOn}
        hiddenColumns={hiddenColumns}
        loading={loading}
        onSelectQuery={setSelectedQuery}
        onPriceClick={setPriceCard}
        onDraftClick={card => setEditDraftCard(card)}
        onWatiOpen={openChatByPhone}
        onSendClick={card => setSendCard(card)}
        onUrgencyChange={handleUrgencyChange}
        chatDrawerPinned={chatDrawerPinned}
        chatDrawerWidth={chatDrawerWidth}
        activeChats={activeChats}
        setActiveChats={setActiveChats}
        chatsLoading={chatsLoading}
        chatsLoadingMore={chatsLoadingMore}
        hasMoreChats={hasMoreChats}
        chatSearch={chatSearch}
        setChatSearch={setChatSearch}
        chatSearchFilter={chatSearchFilter}
        setChatSearchFilter={setChatSearchFilter}
        fetchActiveChats={fetchActiveChats}
        loadMoreChats={loadMoreChats}
        selectedChat={selectedChat}
        setSelectedChat={setSelectedChat}
        queryMatchesByPhone={queryMatchesByPhone}
        scrollToSingleQuery={scrollToSingleQuery}
        flashingChatIds={flashingChatIds}
        authState={authState}
        onBulkTemplateOpen={() => {
          setBulkTmplOpen(true);
          if (bulkTemplates.length > 0) return;
          setBulkTmplLoading(true);
          _fetchTemplates(authState.id).then(list => { setBulkTemplates(list); setBulkTmplLoading(false); });
        }}
        sendingCards={sendingCards}
        activeApprovalsMap={activeApprovalsMap}
        onPaymentClick={setPaymentCard}
        onReassignClick={card => setReassignCard(card)}
        onUnparkClick={card => setUnparkCard(card)}
        onCancelClick={card => setCancelCard({ queryPk: card.id ?? card.query_pk, queryId: card.query_id })}
      />

      <WorkflowTaskDrawer
        queryPk={selectedQuery?.id ?? selectedQuery?.query_pk ?? null}
        onClose={() => setSelectedQuery(null)}
        onSent={handleQuerySent}
        onReassignClick={pk => setReassignCard({ id: pk, query_pk: pk })}
        onUnparkClick={pk => setUnparkCard({ id: pk, query_pk: pk })}
        onCancelClick={(pk, qid) => setCancelCard({ queryPk: pk, queryId: qid })}
      />

      <ReassignModal
        open={!!reassignCard}
        queryPk={reassignCard?.id ?? reassignCard?.query_pk ?? null}
        onClose={() => setReassignCard(null)}
        onSuccess={() => setReassignCard(null)}
      />

      <UnparkModal
        open={!!unparkCard}
        queryPk={unparkCard?.id ?? unparkCard?.query_pk ?? null}
        onClose={() => setUnparkCard(null)}
        onSuccess={() => setUnparkCard(null)}
      />

      <CancelModal
        open={!!cancelCard}
        queryPk={cancelCard?.queryPk ?? null}
        queryId={cancelCard?.queryId ?? null}
        onClose={() => setCancelCard(null)}
        onSuccess={() => setCancelCard(null)}
      />

      <PriceUpdateDrawer
        card={priceCard}
        open={!!priceCard}
        onClose={() => setPriceCard(null)}
        userId={authState?.id}
        authState={authState}
        onPartsLoaded={handlePartsLoaded}
        approvalDecidedTick={approvalDecidedTick}
        userMap={Object.fromEntries(agentOptions.map(u => [u.value, u.label]))}
      />

      <PaymentLinkModal
        open={!!paymentCard}
        onClose={() => setPaymentCard(null)}
        card={paymentCard}
        userId={authState?.id}
        authState={authState}
      />

      <EditDraftDrawer
        queryPk={editDraftCard?.id ?? null}
        open={!!editDraftCard}
        onClose={() => setEditDraftCard(null)}
        onSaved={(updatedTask, openSend) => {
          fetchQueries();
          setEditDraftCard(null);
          if (openSend && updatedTask?.task_id) setSendCard({ id: updatedTask.task_id, ...updatedTask });
        }}
      />

      <SendToSuppliersModal
        queryPk={sendCard?.id ?? null}
        task={sendCard}
        open={!!sendCard}
        userId={authState?.id}
        onClose={() => setSendCard(null)}
        onSent={count => handleQuerySent(sendCard?.id, count)}
        onEditClick={() => { setEditDraftCard(sendCard); setSendCard(null); }}
      />

      {/* ── Bulk Template Send flow ─────────────────────────────────────────── */}
      <TemplatesModal
        open={bulkTmplOpen}
        onClose={() => { setBulkTmplOpen(false); setBulkTemplate(null); setBulkSummaryOpen(false); setBulkRecipients([]); }}
        templates={bulkTemplates}
        loading={bulkTmplLoading}
        onSelect={t => setBulkTemplate(t)}
      />
      <BulkTemplatePreviewModal
        open={!!bulkTemplate}
        onClose={() => setBulkTemplate(null)}
        template={bulkTemplate}
        authState={authState}
        zIndex={1010}
        onNext={recs => { setBulkRecipients(recs); setBulkSummaryOpen(true); }}
      />
      <BulkSummaryModal
        open={bulkSummaryOpen}
        onClose={() => setBulkSummaryOpen(false)}
        onDone={() => { setBulkSummaryOpen(false); setBulkTemplate(null); setBulkRecipients([]); setBulkTmplOpen(false); }}
        afterClose={() => {}}
        template={bulkTemplate}
        recipients={bulkRecipients}
        authState={authState}
        zIndex={1020}
      />

      <ActiveChatDrawer
        chat={selectedChat}
        pinned={chatDrawerPinned}
        onPinToggle={() => setChatDrawerPinned(v => !v)}
        onClose={() => setSelectedChat(null)}
        onWidthChange={setChatDrawerWidth}
        onChatPatch={fields => {
          if (!selectedChat) return;
          const patch = c => c.id === selectedChat.id ? { ...c, ...fields } : c;
          setActiveChats(cs => cs.map(patch));
          setSelectedChat(c => patch(c));
        }}
        onQueryCreated={(card, openSend) => {
          setQuerySet(prev => [card, ...prev]);
          if (openSend) setSendCard(card);
        }}
      />

      {/* ── Manual "New Query" flow ─────────────────────────────────────────── */}
      <NewQueryModal
        open={newQueryPickerOpen}
        onClose={() => setNewQueryPickerOpen(false)}
        onSelect={cust => setNewQueryCust(cust)}
      />
      <CustomerEditDrawer
        open={!!newQueryCust}
        customer={newQueryCust}
        defaultTab="queries"
        defaultCreateOpen
        onClose={() => setNewQueryCust(null)}
        onSaved={() => {}}
        onQueryCreated={(card, openSend) => {
          setQuerySet(prev => [card, ...prev]);
          setNewQueryCust(null);
          if (openSend) setSendCard(card);
        }}
      />

    </div>
  );
}

export default QuerysetV4;
